import React, {useEffect, useState} from 'react';
import moment from 'moment';
import _ from 'lodash';
import {useDatabase} from '@nozbe/watermelondb/react';
import Graph from '../../graph';
import {normalizeDay} from 'src/common-utils/normalizeDay';

const TrialByTrialProgressGraph = ({
  program = {},
  data = [], // analysis map/object coming from parent
  sets = [],
  envs = [],
  sessions = [],
  session = null,
  target = {},
  children,
  title = null,
  extras = {},
  startDate = '',
  endDate = '',
  byDay = false,
  filteredEnvItems = [],
  shouldShowSummary = false,
  summaryOnly = false,
  targets = [],
  events = [],
  allSets = {},
  recentSetsByTargetId = {},
  shouldShowMenu = true,
  sessionMessage = false,
  reportScreen = false,
}: any) => {
  const database = useDatabase();
  const [analysis, setAnalysis] = useState<any>({});

  // Build boundaries SAFELY (inclusive)
  const sb = normalizeDay(startDate);
  const eb = normalizeDay(endDate);
  const startBoundary = sb && sb.isValid() ? sb.clone().startOf('day') : null;
  const endBoundary = eb && eb.isValid() ? eb.clone().endOf('day') : null;
  const hasBounds = !!(startBoundary && endBoundary);

  const inRange = (ts: any) => {
    if (!hasBounds) {
      return true;
    }
    const m = normalizeDay(ts);
    if (!m || !m.isValid()) {
      return false;
    }
    return m.isSameOrAfter(startBoundary!) && m.isSameOrBefore(endBoundary!);
  };

  // Window filter for envs
  const sortedEnvs = envs.filter((env: any) => inRange(env?.date));

  // If byDay: one session per calendar day; else: use sets.
  const dailySessions = Object.values(_.groupBy(sessions, 'sessionDate')).map(
    (obj: any) => obj[0],
  );

  const analyzedSets = sessionMessage
    ? [sets[0]]
    : reportScreen
    ? sets.filter((set: any) => inRange(set?.date))
    : sets;

  const analyzedData = byDay ? dailySessions : analyzedSets;

  const calculateData = async () => {
    // ---- 1) Bucket by calendar day ----
    type Bucket = {
      points: Array<{
        average: number;
        state: string;
        collectors?: string[];
        startTimestamp: number;
        target?: string;
      }>;
      change: Array<{date: number; title: string; description?: string}>;
      factor: Array<{date: number; title: string; description?: string}>;
    };
    const buckets: Record<string, Bucket> = {};
    const ensure = (key: string) => {
      if (!buckets[key]) {
        buckets[key] = {points: [], change: [], factor: []};
      }
      return buckets[key];
    };

    // NOTE: parent passes `data` (analysis)
    const analysisMap: any = data;

    // 1a) Program points → bucket by day
    for (const datum of analyzedData) {
      // Resolve a canonical timestamp for the row
      const rawTs = sessionMessage
        ? moment(session?.sessionDate).valueOf()
        : datum?.session_date ??
          datum?.sessionDate ??
          datum?.startTimestamp ??
          datum?.timestamp ??
          datum?.date;

      const m = normalizeDay(rawTs);
      if (!m || !m.isValid()) {
        continue;
      }

      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      // Resolve analysis object similar to original logic
      let obj: any = null;
      if (analysisMap) {
        if (analysisMap[datum?.id]) {
          obj = analysisMap[datum?.id];
        } else if (analysisMap[datum?.sessionId]) {
          obj = analysisMap[datum?.sessionId];
        } else if (analysisMap[datum?.session_id]) {
          obj = analysisMap[datum?.session_id];
        } else if (analysisMap[datum?.sessionDate]) {
          obj = analysisMap[datum?.sessionDate];
        } else if (analysisMap?.sessionId) {
          obj = analysisMap;
        }
      }
      // Skip raw session rows with no analysis
      if (!obj && datum?.type === 'session') {
        continue;
      }
      if (!obj) {
        continue;
      }

      // Resolve collectors to names
      let collectors: string[] = [];
      if (Array.isArray(obj?.collectors) && obj.collectors.length) {
        collectors = await Promise.all(
          obj.collectors.map(async (collectorId: string) => {
            if (collectorId !== 'System') {
              try {
                const u = await database.get('users').find(collectorId);
                return `${u?.firstName} ${u?.lastName}`;
              } catch {
                return 'System';
              }
            }
            return 'System';
          }),
        );
      }

      const dayKey = dayStart.format('YYYY-MM-DD');
      ensure(dayKey).points.push({
        average: isNaN(obj?.average) ? 0 : Number(obj?.average),
        state: obj?.state || datum?.state || '',
        collectors,
        startTimestamp: dayStart.valueOf(),
        target: target?.name,
      });
    }

    // 1b) Environmental items → bucket by day & type
    for (const env of sortedEnvs) {
      const m = normalizeDay(env?.date);
      if (!m || !m.isValid()) {
        continue;
      }
      const dayStart = m.clone().startOf('day');
      if (!inRange(dayStart.valueOf())) {
        continue;
      }

      const dayKey = dayStart.format('YYYY-MM-DD');
      const item = {
        date: dayStart.valueOf(),
        title: env?.title,
        description: env?.description,
      };
      const b = ensure(dayKey);
      if (env?.type === 'change') {
        b.change.push(item);
      } else if (env?.type === 'factor') {
        b.factor.push(item);
      } else {
        b.factor.push(item);
      } // default bucket if type missing
    }

    // ---- 2) Sort unique day keys ----
    const sortedKeys = Object.keys(buckets).sort(
      (a, b) =>
        moment(a, 'YYYY-MM-DD').valueOf() - moment(b, 'YYYY-MM-DD').valueOf(),
    );

    if (!sortedKeys.length) {
      setAnalysis({data: [], envLineData: [], phaseChanges: [], lines: []});
      return;
    }

    // ---- 3) Build x-axis data (exactly one entry per day) ----
    const xAxisData = sortedKeys.map(key => ({
      startTimestamp: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
      key,
    }));

    // ---- 4) Build daily program points (avg if multiple per day) ----
    const dailyPoints = sortedKeys
      .map((key, idx) => {
        const b = buckets[key];
        if (!b.points.length) {
          return null;
        }

        const avg =
          b.points.reduce((s, p) => s + (Number(p.average) || 0), 0) /
          b.points.length;

        // mode state for the day
        const stateCounts = _.countBy(b.points.map(p => p.state || ''));
        const state =
          Object.keys(stateCounts).sort(
            // eslint-disable-next-line @typescript-eslint/no-shadow
            (a, b) => stateCounts[b] - stateCounts[a],
          )[0] || '';

        // de-dupe collectors
        const collectors = _.uniq(b.points.flatMap(p => p.collectors || []));

        return {
          x: idx + 1,
          y: avg,
          date: moment(key, 'YYYY-MM-DD').startOf('day').valueOf(),
          target: target?.name,
          collectors,
          state,
        };
      })
      .filter(Boolean) as Array<{
      x: number;
      y: number;
      date: number;
      target?: string;
      collectors: string[];
      state: string;
    }>;

    // ---- 5) Segment by state into `lines` (type: 'point') + phaseChanges ----
    const lines: any[] = [];
    const phaseChanges: any[] = [];

    if (dailyPoints.length) {
      let currentState = {
        state: dailyPoints[0].state,
        aggregate: dailyPoints[0].x,
        num: 1,
        index: dailyPoints[0].x,
      };
      let latestLine = {
        state: dailyPoints[0].state,
        points: [dailyPoints[0]],
        type: 'point' as const,
      };

      for (let i = 1; i < dailyPoints.length; i++) {
        const p = dailyPoints[i];
        if (p.state === latestLine.state) {
          latestLine.points.push(p);
          currentState = {
            ...currentState,
            aggregate: currentState.aggregate + p.x,
            num: currentState.num + 1,
          };
        } else {
          phaseChanges.push({
            line: currentState.index,
            x: currentState.aggregate / currentState.num,
            state: latestLine.state,
          });
          lines.push(latestLine);

          latestLine = {state: p.state, points: [p], type: 'point'};
          currentState = {
            state: p.state,
            aggregate: p.x,
            num: 1,
            index: p.x,
          };
        }
      }
      // close out
      lines.push(latestLine);
      phaseChanges.push({
        line: currentState.index,
        x: currentState.aggregate / currentState.num,
        state: currentState.state,
      });
    }

    // ---- 6) Add unified environmental overlay entries (type: 'env') per day ----
    sortedKeys.forEach((key, idx) => {
      const b = buckets[key];
      const x = idx + 1;
      const envItems = [
        ...b.change.map(i => ({...i, type: 'change'})),
        ...b.factor.map(i => ({...i, type: 'factor'})),
      ];
      if (envItems.length) {
        lines.push({
          x,
          y: 50,
          type: 'env',
          values: {
            [key]: envItems, // 'YYYY-MM-DD'
          },
        });
      }
    });

    setAnalysis({
      data: xAxisData,
      envLineData: [],
      phaseChanges,
      lines,
    });
  };

  useEffect(() => {
    calculateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    target?.id,
    sets,
    sessions,
    envs,
    data,
    program,
    startDate,
    endDate,
    byDay,
    sessionMessage,
    reportScreen,
  ]);

  return (
    <Graph
      yAxisLabel={'% Correct'}
      slot={children}
      data={analysis.data}
      program={program}
      envs={analysis.envLineData}
      phaseChanges={analysis.phaseChanges}
      lines={analysis.lines}
      title={title}
      extras={extras}
      targets={targets}
      events={events}
      sessions={sessions}
      sets={sets}
      filteredEnvItems={filteredEnvItems}
      shouldShowSummary={shouldShowSummary}
      summaryOnly={summaryOnly}
      allSets={allSets}
      reportScreen={reportScreen}
      recentSetsByTargetId={recentSetsByTargetId}
      shouldShowMenu={shouldShowMenu}
      startDate={startDate}
      endDate={endDate}
    />
  );
};

export default React.memo(TrialByTrialProgressGraph);
